'use client';

import React from 'react';
import { VoiceParticipant } from './useVoiceCall';

// Комментарий на русском: список участников голосового чата

interface VoiceParticipantListProps {
  participants: VoiceParticipant[];
  currentUserId?: number;
  maxVisible?: number;
}

export function VoiceParticipantList({ participants, currentUserId, maxVisible = 50 }: VoiceParticipantListProps) {
  const visibleParticipants = participants.slice(0, maxVisible);
  const hiddenCount = Math.max(0, participants.length - maxVisible);

  if (participants.length === 0) {
    return (
      <div className="text-sm text-site-muted dark:text-site-muted text-center py-4">
        Нет участников
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs text-site-muted dark:text-site-muted mb-1">
        Участники ({participants.length})
      </div>
      <div className="flex flex-col gap-1 max-h-60 overflow-y-auto">
        {visibleParticipants.map((participant) => (
          <ParticipantItem
            key={participant.user_id}
            participant={participant}
            isCurrentUser={participant.user_id === currentUserId}
          />
        ))}
        {hiddenCount > 0 && (
          <div className="text-xs text-site-muted dark:text-site-muted text-center py-2">
            +{hiddenCount} ещё
          </div>
        )}
      </div>
    </div>
  );
}

interface ParticipantItemProps {
  participant: VoiceParticipant;
  isCurrentUser: boolean;
}

function ParticipantItem({ participant, isCurrentUser }: ParticipantItemProps) {
  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg
        ${isCurrentUser ? 'bg-site-accent dark:bg-site-accent/10' : 'bg-site-raised dark:bg-site-surface/50'}
        ${participant.speaking ? 'ring-2 ring-site-success' : ''}`}
    >
      {/* Аватар */}
      <div className="relative flex-shrink-0">
        {participant.avatar_url ? (
          <img
            src={participant.avatar_url}
            alt={participant.display_name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-site-raised dark:bg-site-raised flex items-center justify-center">
            <span className="text-xs font-medium text-site-muted dark:text-site-secondary">
              {participant.display_name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        {/* Индикатор говорения */}
        {participant.speaking && (
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-site-success rounded-full border-2 border-site-border dark:border-site-border" />
        )}
      </div>

      {/* Имя и статус */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-site-muted dark:text-site-text truncate">
            {participant.display_name}
          </span>
          {participant.role === 'host' && (
            <span className="text-[10px] px-1 py-0.5 bg-site-warning dark:bg-site-warning/10 text-site-warning dark:text-site-warning rounded">
              Хост
            </span>
          )}
          {isCurrentUser && (
            <span className="text-[10px] text-site-muted dark:text-site-muted">(вы)</span>
          )}
        </div>
      </div>

      {/* Иконки статуса */}
      <div className="flex items-center gap-1">
        {participant.muted_self && (
          <svg className="w-4 h-4 text-site-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
          </svg>
        )}
        {participant.deafened_self && (
          <svg className="w-4 h-4 text-site-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M5.636 5.636a9 9 0 1012.728 12.728M5.636 5.636L18.364 18.364M5.636 5.636l12.728 12.728" />
          </svg>
        )}
      </div>
    </div>
  );
}
